/**
 * QNNEAL Swarm Orchestrator — Voice Copilot with 9 Client Tools
 * Uses @elevenlabs/react v1.x with ConversationProvider + useConversationClientTool hooks.
 * Must be rendered inside <ConversationProvider> (set up in App.js).
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useConversation,
  useConversationClientTool,
} from '@elevenlabs/react';

const AGENT_ID = 'agent_6301knsv48zpe85904ec6d0ckb6h';

// ── CLIENT TOOL HANDLERS (9 tools matching ElevenLabs dashboard) ─────

function useClientTools() {
  // Tool 1: qnnealNavigateToPage
  useConversationClientTool('qnnealNavigateToPage', (params) => {
    const { page } = params;
    const routeMap = {
      dashboard: '/dashboard', home: '/dashboard', optimize: '/dashboard',
      overview: '/dashboard?tab=overview',
      providers: '/dashboard?tab=provider-mgmt', 'provider-mgmt': '/dashboard?tab=provider-mgmt',
      models: '/dashboard?tab=model-catalog', 'model-catalog': '/dashboard?tab=model-catalog',
      status: '/dashboard?tab=status',
      billing: '/dashboard?tab=settings', settings: '/dashboard?tab=settings',
      'api-keys': '/dashboard?tab=settings', vault: '/dashboard?tab=settings',
      optimization: '/dashboard?tab=optimization-engine', 'optimization-engine': '/dashboard?tab=optimization-engine', optimizer: '/dashboard?tab=optimization-engine',
      usage: '/dashboard?tab=usage',
      team: '/dashboard?tab=team-mgmt', 'team-mgmt': '/dashboard?tab=team-mgmt',
      profile: '/dashboard?tab=profile',
      analytics: '/dashboard?tab=analytics', savings: '/dashboard?tab=analytics',
      workflows: '/dashboard?tab=workflows',
      swarm: '/dashboard?tab=swarm', 'agent-swarm': '/dashboard?tab=swarm', 'swarm-builder': '/dashboard?tab=swarm',
      haven: '/dashboard?tab=haven-portal', 'haven-portal': '/dashboard?tab=haven-portal',
      harvest: '/dashboard?tab=harvest-portal', 'harvest-portal': '/dashboard?tab=harvest-portal',
      'cross-vertical': '/dashboard?tab=cross-vertical', intelligence: '/dashboard?tab=cross-vertical',
      'tenant-admin': '/dashboard?tab=tenant-admin',
      'sales-swarm': '/dashboard?tab=sales-swarm',
      pricing: '/pricing', security: '/security', privacy: '/privacy', terms: '/terms',
    };
    const route = routeMap[page?.toLowerCase()] || `/${page}`;
    try {
      if (window.__qnnealNavigate) window.__qnnealNavigate(route);
      else window.location.href = route;
      return `Navigated to ${page}`;
    } catch (err) {
      return `Navigation failed: ${err.message}`;
    }
  });

  // Tool 2: qnnealClickElement
  useConversationClientTool('qnnealClickElement', (params) => {
    const { selector, text } = params;
    try {
      let element = null;
      if (selector) {
        element = document.querySelector(selector) || document.querySelector(`[data-testid="${selector}"]`);
      }
      if (!element && text) {
        const allElements = document.querySelectorAll('button, a, [role="button"], [role="tab"], [role="menuitem"], input[type="submit"]');
        for (const el of allElements) {
          if (el.textContent?.trim().toLowerCase().includes(text.toLowerCase())) { element = el; break; }
        }
      }
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => element.click(), 300);
        return `Clicked element: ${text || selector}`;
      }
      return `Element not found: ${text || selector}`;
    } catch (err) {
      return `Click failed: ${err.message}`;
    }
  });

  // Tool 3: qnnealFillFormField
  useConversationClientTool('qnnealFillFormField', (params) => {
    const { selector, label, placeholder, value } = params;
    try {
      let input = null;
      if (selector) input = document.querySelector(selector) || document.querySelector(`[data-testid="${selector}"]`);
      if (!input && label) {
        const labels = document.querySelectorAll('label');
        for (const lbl of labels) {
          if (lbl.textContent?.trim().toLowerCase().includes(label.toLowerCase())) {
            const forId = lbl.getAttribute('for');
            input = forId ? document.getElementById(forId) : lbl.querySelector('input, textarea, select');
            if (input) break;
          }
        }
      }
      if (!input && (placeholder || label)) {
        const search = placeholder || label;
        input = document.querySelector(`input[placeholder*="${search}" i], textarea[placeholder*="${search}" i]`);
      }
      if (input) {
        input.focus();
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
          || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        if (setter) setter.call(input, value);
        else input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return `Filled "${label || selector}" with "${value}"`;
      }
      return `Form field not found: ${label || selector}`;
    } catch (err) {
      return `Fill failed: ${err.message}`;
    }
  });

  // Tool 4: qnnealScrollPage
  useConversationClientTool('qnnealScrollPage', (params) => {
    const { direction, amount } = params;
    const px = amount || 500;
    try {
      const target = document.querySelector('main') || document.documentElement;
      if (direction === 'up') target.scrollBy({ top: -px, behavior: 'smooth' });
      else if (direction === 'down') target.scrollBy({ top: px, behavior: 'smooth' });
      else if (direction === 'top') target.scrollTo({ top: 0, behavior: 'smooth' });
      else if (direction === 'bottom') target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
      return `Scrolled ${direction} by ${px}px`;
    } catch (err) {
      return `Scroll failed: ${err.message}`;
    }
  });

  // Tool 5: qnnealHighlightElement
  useConversationClientTool('qnnealHighlightElement', (params) => {
    const { selector, text, duration } = params;
    try {
      document.querySelectorAll('.qnneal-highlight').forEach(el => {
        el.style.outline = ''; el.style.outlineOffset = ''; el.classList.remove('qnneal-highlight');
      });
      let element = null;
      if (selector) element = document.querySelector(selector) || document.querySelector(`[data-testid="${selector}"]`);
      if (!element && text) {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
        while (walker.nextNode()) {
          if (walker.currentNode.children.length === 0 && walker.currentNode.textContent?.trim().toLowerCase().includes(text.toLowerCase())) {
            element = walker.currentNode; break;
          }
        }
      }
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('qnneal-highlight');
        element.style.outline = '3px solid #2792dc';
        element.style.outlineOffset = '4px';
        element.style.transition = 'outline 0.3s ease';
        const ms = (duration || 5) * 1000;
        setTimeout(() => { element.style.outline = ''; element.style.outlineOffset = ''; element.classList.remove('qnneal-highlight'); }, ms);
        return `Highlighted element: ${text || selector}`;
      }
      return `Element not found for highlighting: ${text || selector}`;
    } catch (err) {
      return `Highlight failed: ${err.message}`;
    }
  });

  // Tool 6: qnnealTakeScreenshot
  useConversationClientTool('qnnealTakeScreenshot', () => {
    try {
      const visibleElements = [];
      document.querySelectorAll('button:not([disabled]), a[href], [role="button"], [role="tab"]').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= window.innerHeight && rect.width > 0) {
          const t = el.textContent?.trim().substring(0, 40);
          if (t) visibleElements.push(`${el.tagName.toLowerCase()}: "${t}"`);
        }
      });
      return JSON.stringify({
        url: window.location.href, title: document.title,
        timestamp: new Date().toISOString(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        visibleElements: visibleElements.slice(0, 20),
      });
    } catch (err) {
      return `Screenshot failed: ${err.message}`;
    }
  });

  // Tool 7: qnnealReadPageContent
  useConversationClientTool('qnnealReadPageContent', (params) => {
    const { section } = params || {};
    try {
      let container = null;
      if (section) {
        const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
        for (const h of headings) {
          if (h.textContent?.toLowerCase().includes(section.toLowerCase())) { container = h.closest('section') || h.parentElement; break; }
        }
        if (!container) container = document.getElementById(section) || document.querySelector(`[data-section="${section}"]`);
      }
      if (!container) container = document.querySelector('main') || document.querySelector('[role="main"]') || document.body;

      // Check for execution results
      const resultsViewer = document.querySelector('[data-testid="universal-results-viewer"]');
      if (resultsViewer) {
        const execBrief = document.querySelector('[data-testid="executive-briefing"]')?.innerText?.slice(0, 1500) || '';
        const actionItems = document.querySelector('[data-testid="action-items-panel"]')?.innerText?.slice(0, 800) || '';
        const pipelineInfo = document.querySelector('[data-testid="pipeline-agent-details"]')?.innerText?.slice(0, 500) || '';
        return JSON.stringify({
          page: 'Execution Results',
          hasResults: true,
          executiveBriefing: execBrief,
          actionItems: actionItems,
          pipelineInfo: pipelineInfo,
        });
      }

      // Check for execution progress
      const progressBar = document.querySelector('[data-testid="execution-progress"]');
      if (progressBar) {
        return JSON.stringify({
          page: 'Swarm Execution In Progress',
          progress: progressBar.innerText?.slice(0, 200) || 'Running...',
        });
      }

      const content = container?.innerText?.slice(0, 2000) || 'No content found';
      const cards = document.querySelectorAll('[class*="card"], [class*="Card"]');
      const metrics = [];
      cards.forEach(card => {
        const title = card.querySelector('h3, h4, [class*="title"], [class*="Title"]');
        const value = card.querySelector('[class*="value"], [class*="Value"], .text-2xl, .text-3xl, .text-4xl');
        if (title && value) metrics.push(`${title.textContent?.trim()}: ${value.textContent?.trim()}`);
      });
      return JSON.stringify({ page: document.title, url: window.location.pathname, metrics: metrics.slice(0, 10), content: content.slice(0, 1000) });
    } catch (err) {
      return `Read failed: ${err.message}`;
    }
  });

  // Tool 8: qnnealExecuteWorkflow
  useConversationClientTool('qnnealExecuteWorkflow', (params) => {
    const { workflow, prompt, parameters: wfParams } = params;
    const userPrompt = prompt || workflow || wfParams?.prompt || '';
    try {
      // Navigate to swarm tab first if not already there
      const swarmTab = document.querySelector('[data-testid="swarm-tab"]') ||
        Array.from(document.querySelectorAll('button, [role="tab"]')).find(el => el.textContent?.toLowerCase().includes('agent swarm'));
      if (swarmTab) swarmTab.click();

      // Dispatch execution event with prompt
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('qnneal:executeWorkflow', {
          detail: { prompt: userPrompt, workflow, parameters: wfParams, timestamp: Date.now() },
          bubbles: true,
        }));
      }, 500);

      return `Swarm execution initiated with prompt: "${userPrompt}". Watch the canvas — agents are lighting up now.`;
    } catch (err) {
      return `Execution failed: ${err.message}`;
    }
  });

  // Tool 9: qnnealCollapseWidget
  useConversationClientTool('qnnealCollapseWidget', () => {
    try {
      document.dispatchEvent(new CustomEvent('qnneal:collapseWidget', { bubbles: true }));
      return 'Widget collapsed. The user can now see the full page.';
    } catch (err) {
      return `Collapse failed: ${err.message}`;
    }
  });
}

// ── SWARM ORCHESTRATOR UI ─────────────────────────────────────────────

export default function SwarmOrchestrator() {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const conversation = useConversation({
    onConnect: ({ conversationId }) => {
      console.log('[SwarmOrchestrator] Connected! ID:', conversationId);
      setError(null);
    },
    onDisconnect: (details) => {
      console.log('[SwarmOrchestrator] Disconnected:', JSON.stringify(details));
      if (details?.reason === 'error') {
        setError(`Disconnected: ${details.message || details.closeReason || 'Connection error'}`);
      }
    },
    onMessage: (message) => {
      console.log('[SwarmOrchestrator] Message:', message);
      setMessages(prev => [...prev, {
        role: message.source || message.role || 'agent',
        text: message.message || message.content || '',
      }]);
    },
    onError: (errorMsg, context) => {
      console.error('[SwarmOrchestrator] Error:', errorMsg, context);
      setError(`Error: ${errorMsg}`);
    },
    onStatusChange: ({ status: s }) => {
      console.log('[SwarmOrchestrator] Status changed:', s);
    },
    onModeChange: ({ mode }) => {
      console.log('[SwarmOrchestrator] Mode:', mode);
    },
  });

  const { status, isSpeaking, startSession, endSession, sendUserMessage } = conversation;

  // Register all 9 client tools
  useClientTools();

  // Listen for collapse events
  useEffect(() => {
    const handleCollapse = () => setIsExpanded(false);
    document.addEventListener('qnneal:collapseWidget', handleCollapse);
    return () => document.removeEventListener('qnneal:collapseWidget', handleCollapse);
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleToggle = useCallback(async () => {
    if (status === 'connected' || status === 'connecting') {
      // End active session
      try { await endSession(); } catch (e) { /* ignore */ }
      setIsExpanded(false);
      setError(null);
      return;
    }

    // Start new session immediately
    setIsExpanded(true);
    setError(null);
    setMessages([]);

    try {
      console.log('[SwarmOrchestrator] Connecting via WebSocket...');
      await startSession({
        agentId: AGENT_ID,
        connectionType: 'websocket',
      });
      console.log('[SwarmOrchestrator] Connected');
    } catch (err) {
      console.error('[SwarmOrchestrator] Connection failed:', err);
      setError(`Connection failed: ${err?.message || err}`);
    }
  }, [status, startSession, endSession]);

  const handleSendText = useCallback(() => {
    if (!textInput.trim() || status !== 'connected') return;
    setMessages(prev => [...prev, { role: 'user', text: textInput }]);
    sendUserMessage(textInput);
    setTextInput('');
  }, [textInput, status, sendUserMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); }
  }, [handleSendText]);

  const statusColor = status === 'connected' ? '#10b981' : status === 'connecting' ? '#f59e0b' : '#6b7280';
  const orbColor = status === 'connected' && isSpeaking ? '#2792dc' : status === 'connected' ? '#10b981' : '#1a1a2e';

  // P1 — keep the voice copilot out of the cinematic investor explainer scenes.
  if (/^\/investor\/explainer(\/|$)/.test(location.pathname)) {
    return null;
  }

  // Keep the floating voice FAB out of the Universal Tenant Workspace (/app) so it
  // never overlaps the chat composer / send button on smaller viewports.
  if (location.pathname.startsWith('/app')) {
    return null;
  }

  // Portal embeds — hide the floating voice/mic FAB anywhere the URL carries
  // embed=1 or voice=off (lets any embedded page suppress the copilot).
  const _params = new URLSearchParams(location.search);
  if (_params.get('embed') === '1' || _params.get('voice') === 'off') {
    return null;
  }

  return (
    <>
      {/* Floating Orb */}
      <button
        onClick={handleToggle}
        data-testid="voice-copilot-orb"
        title={status === 'connected' ? 'End voice session' : 'Start QNNEAL Voice Copilot'}
        style={{
          position: 'fixed', bottom: 24, right: 24, width: 56, height: 56,
          borderRadius: '50%', backgroundColor: orbColor,
          border: '2px solid rgba(39,146,220,0.3)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: status === 'connected' ? '0 0 20px rgba(39,146,220,0.4)' : '0 4px 12px rgba(0,0,0,0.3)',
          transition: 'all 0.3s ease', zIndex: 9999,
          animation: isSpeaking ? 'orbPulse 1.5s ease-in-out infinite' : 'none',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
        </svg>
        {status === 'connected' && (
          <span style={{ position: 'absolute', top: -2, right: -2, width: 12, height: 12, borderRadius: '50%', backgroundColor: '#10b981', border: '2px solid #0f0f1a' }} />
        )}
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div data-testid="voice-copilot-panel" data-status={status} style={{
          position: 'fixed', bottom: 92, right: 24, width: 340, maxHeight: 420,
          backgroundColor: '#0f0f1a', border: '1px solid rgba(39,146,220,0.2)',
          borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 9998,
        }}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor }} />
              <span style={{ color: '#e0e0e0', fontSize: 13, fontWeight: 600 }}>QNNEAL Swarm Orchestrator</span>
              <span style={{ color: statusColor, fontSize: 10, fontWeight: 400 }}>{status || 'initializing'}</span>
            </div>
            <button onClick={() => setIsExpanded(false)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>x</button>
          </div>

          {/* Error */}
          {error && <div style={{ padding: '8px 16px', backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 12 }}>{error}</div>}

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {status === 'connected' && messages.length === 0 && <div style={{ color: '#10b981', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Connected. Listening... Speak or type below.</div>}
            {(status === 'connecting') && <div style={{ color: '#f59e0b', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Connecting to ElevenLabs...</div>}
            {(status === 'disconnected' || status === 'disconnecting') && !error && <div style={{ color: '#888', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Disconnected. Click the orb to reconnect.</div>}
            {(!status) && !error && <div style={{ color: '#f59e0b', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Initializing session...</div>}
            {messages.map((msg, i) => (
              <div key={i} style={{
                padding: '8px 12px', borderRadius: 8, fontSize: 13, lineHeight: '1.4',
                backgroundColor: msg.role === 'user' ? 'rgba(39,146,220,0.15)' : 'rgba(255,255,255,0.05)',
                color: msg.role === 'user' ? '#7cc4f0' : '#d0d0d0',
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%',
              }}>{msg.text}</div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Text Input */}
          {status === 'connected' && (
            <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8 }}>
              <input
                type="text" value={textInput} onChange={e => setTextInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Type a command..."
                style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', color: '#e0e0e0', fontSize: 13, outline: 'none' }}
              />
              <button onClick={handleSendText} disabled={!textInput.trim()}
                style={{ backgroundColor: textInput.trim() ? '#2792dc' : '#333', border: 'none', borderRadius: 8, padding: '8px 14px', color: 'white', fontSize: 13, cursor: textInput.trim() ? 'pointer' : 'default' }}>
                Send
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes orbPulse { 0%,100% { box-shadow: 0 0 20px rgba(39,146,220,0.4); } 50% { box-shadow: 0 0 35px rgba(39,146,220,0.7); } }`}</style>
    </>
  );
}
